/**
 * GlobalChatSidebar
 *
 * Chat sidebar that is fully hidden when collapsed — only a floating Martha orb
 * button appears in the bottom-right corner. Clicking the orb (or Cmd+Shift+M)
 * opens the full chat panel.
 *
 * Merges page-registered tabs (e.g. TOC, Comments, Related from DocumentationReader)
 * with an always-present Chat tab. Uses ReaderDetailPanelShell for visual consistency.
 *
 * Keyboard shortcuts:
 *   Cmd+Shift+M — toggle sidebar
 *   Escape — collapse (when not in textarea/input)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { useChatSession } from '../../contexts/ChatSessionContext';
import { useRightPanelRegistry } from '../../contexts/RightPanelRegistryContext';
import { ReaderDetailPanelShell, type TabDefinition } from '../shared/ReaderDetailPanelShell';
import { ChatTabContent, type ChatTabHeaderControls } from './ChatTabContent';

const CHAT_TAB_ID = 'chat';

export function GlobalChatSidebar() {
  const { isExpanded, setExpanded, toggle } = useChatSession();
  const { pageTabs } = useRightPanelRegistry();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<string>(CHAT_TAB_ID);

  // Build merged tab definitions: page tabs (left) + chat tab (rightmost)
  const allTabDefs: TabDefinition[] = useMemo(() => {
    const pageTabDefs: TabDefinition[] = pageTabs.map((t) => ({
      id: t.id,
      icon: t.icon,
      title: t.title,
      badge: t.badge,
      badgeColor: t.badgeColor,
    }));

    const chatTab: TabDefinition = {
      id: CHAT_TAB_ID,
      icon: <MessageSquare size={16} />,
      title: 'Chat',
    };

    return [...pageTabDefs, chatTab];
  }, [pageTabs]);

  // Reset active tab to chat when current tab disappears (page navigated away)
  useEffect(() => {
    const tabIds = new Set(allTabDefs.map((t) => t.id));
    if (!tabIds.has(activeTab)) {
      setActiveTab(CHAT_TAB_ID);
    }
  }, [allTabDefs, activeTab]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+Shift+M — toggle sidebar
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'M') {
        e.preventDefault();
        toggle();
        return;
      }

      // Escape — blur focused input first, then collapse on next press
      if (e.key === 'Escape' && isExpanded) {
        const target = e.target as HTMLElement;
        const tag = target?.tagName;
        if (tag === 'TEXTAREA' || tag === 'INPUT') {
          target.blur();
          return;
        }
        setExpanded(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded, setExpanded, toggle]);

  // Listen for Martha navigate events (from navigate_user tool)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ url: string }>).detail;
      if (detail?.url) navigate(detail.url);
    };
    window.addEventListener('martha:navigate', handler);
    return () => window.removeEventListener('martha:navigate', handler);
  }, [navigate]);

  // When Chat tab is active, hide the shell's default white header bar
  // so only the coral Martha bar shows (no double-header)
  const hideShellHeader = activeTab === CHAT_TAB_ID;

  // Pass tab controls to ChatTabContent so it can render page-tabs in the coral bar
  const headerControls: ChatTabHeaderControls | undefined = useMemo(() => {
    if (allTabDefs.length <= 1) return undefined;
    return {
      tabs: allTabDefs,
      activeTab,
      onTabChange: setActiveTab,
      onCollapse: toggle,
    };
  }, [allTabDefs, activeTab, setActiveTab, toggle]);

  // Render active tab content
  const renderContent = useCallback(() => {
    if (activeTab === CHAT_TAB_ID) {
      return <ChatTabContent headerControls={headerControls} />;
    }
    const pageTab = pageTabs.find((t) => t.id === activeTab);
    if (pageTab) {
      return <>{pageTab.render()}</>;
    }
    return null;
  }, [activeTab, pageTabs, headerControls]);

  // When collapsed, render only a floating Martha orb — no sidebar strip
  if (!isExpanded) {
    return (
      <button
        onClick={toggle}
        className="fixed bottom-5 right-5 z-50 w-11 h-11 rounded-full flex items-center justify-center
                   text-white shadow-lg hover:shadow-xl hover:scale-110 active:scale-95
                   transition-all duration-200 select-none"
        style={{ background: 'linear-gradient(135deg, rgb(var(--c-coral-500, 217 127 111)) 0%, rgb(var(--c-coral-600, 198 102 86)) 100%)' }}
        title="Open Martha (⌘⇧M)"
      >
        <svg width="16" height="13" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M1 10V1L7 7.5L13 1V10" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    );
  }

  return (
    <ReaderDetailPanelShell
      tabs={allTabDefs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      isCollapsed={false}
      onToggleCollapse={toggle}
      alwaysVisible
      resizable
      width={360}
      minWidth={300}
      maxWidth={520}
      widthStorageKey="martha_chat_sidebar_width"
      hideHeader={hideShellHeader}
      fillContent={activeTab === CHAT_TAB_ID}
    >
      {renderContent()}
    </ReaderDetailPanelShell>
  );
}
